import type { TBibleBook, TScripture, TScriptureRef } from '../types';
import { BIBLE_BOOKS, chapterCount, findBibleBook, verseCount } from './bibleBooks';

/** A reference with no refs, no free text, and no books. Deep-frozen: it is a shared default, so a push into it throws. */
export const EMPTY_SCRIPTURE: TScripture = Object.freeze({
  refs: Object.freeze([]) as unknown as TScriptureRef[],
  freeform: '',
  books: Object.freeze([]) as unknown as TBibleBook[],
});

const CANON_INDEX: ReadonlyMap<TBibleBook, number> = new Map(
  BIBLE_BOOKS.map((book, index) => [book.code, index])
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isIntegerInRange = (value: unknown, min: number, max: number): value is number =>
  Number.isInteger(value) && (value as number) >= min && (value as number) <= max;

/** Validates one stored ref against the versification table; null when it is out of range or malformed. */
const coerceScriptureRef = (raw: unknown): TScriptureRef | null => {
  if (!isRecord(raw)) return null;
  const book = findBibleBook(raw.book);
  if (!book) return null;

  const { chapterStart, chapterEnd } = raw;
  // A missing verse key reads as a whole chapter, so a hand-edited ref is not silently dropped.
  const verseStart = raw.verseStart ?? null;
  const verseEnd = raw.verseEnd ?? null;
  if (!isIntegerInRange(chapterStart, 1, book.chapters)) return null;
  if (!isIntegerInRange(chapterEnd, chapterStart, book.chapters)) return null;

  const base = {
    book: book.code,
    bookName: typeof raw.bookName === 'string' && raw.bookName ? raw.bookName : book.name,
    chapterStart,
    chapterEnd,
  };
  if (verseStart === null && verseEnd === null) {
    return { ...base, verseStart: null, verseEnd: null };
  }
  if (!isIntegerInRange(verseStart, 1, verseCount(book.code, chapterStart))) return null;
  const minEnd = chapterStart === chapterEnd ? verseStart : 1;
  if (!isIntegerInRange(verseEnd, minEnd, verseCount(book.code, chapterEnd))) return null;
  return { ...base, verseStart, verseEnd };
};

/** The unique books of the refs, in canon order. */
export const scriptureBooks = (refs: readonly TScriptureRef[]): TBibleBook[] =>
  [...new Set(refs.map((ref) => ref.book))].sort(
    (a, b) => (CANON_INDEX.get(a) ?? 0) - (CANON_INDEX.get(b) ?? 0)
  );

/** Reads any stored value as a TScripture and never throws: invalid refs drop, books recompute, and a non-record gives a fresh empty value. */
export const coerceScripture = (raw: unknown): TScripture => {
  // The string fallback guards servicePlans and bulletins, whose rules do not enforce the map.
  if (typeof raw === 'string') return { refs: [], freeform: raw, books: [] };
  if (!isRecord(raw)) return { refs: [], freeform: '', books: [] };

  const refs = Array.isArray(raw.refs)
    ? raw.refs.map(coerceScriptureRef).filter((ref): ref is TScriptureRef => ref !== null)
    : [];
  return {
    refs,
    freeform: typeof raw.freeform === 'string' ? raw.freeform : '',
    books: scriptureBooks(refs),
  };
};

/** The printed book name: "Psalm" for a single psalm, the stored snapshot otherwise. */
const printedBookName = (ref: TScriptureRef): string =>
  ref.book === 'Ps' && ref.chapterStart === ref.chapterEnd ? 'Psalm' : ref.bookName;

/** The verse span of one chapter: `16`, or `1-17`. */
const verseSpan = (start: number, end: number): string =>
  start === end ? `${start}` : `${start}-${end}`;

/**
 * Prints one ref with ASCII hyphens, e.g. `Romans 8:1-17`, `Romans 8-9`, `Romans 8:1-9:5`,
 * `Psalm 23`. A one-chapter book prints verses only (`Jude 3`), or its bare name when whole.
 */
export const formatScriptureRef = (ref: TScriptureRef): string => {
  const { chapterStart, chapterEnd, verseStart, verseEnd } = ref;
  const name = printedBookName(ref);
  const hasVerses = verseStart !== null && verseEnd !== null;
  if (chapterCount(ref.book) === 1) {
    return hasVerses ? `${name} ${verseSpan(verseStart, verseEnd)}` : name;
  }
  if (!hasVerses) {
    return chapterStart === chapterEnd
      ? `${name} ${chapterStart}`
      : `${name} ${chapterStart}-${chapterEnd}`;
  }
  return chapterStart === chapterEnd
    ? `${name} ${chapterStart}:${verseSpan(verseStart, verseEnd)}`
    : `${name} ${chapterStart}:${verseStart}-${chapterEnd}:${verseEnd}`;
};

/** Prints a reference: its refs, then any freeform text, joined by `; `. */
export const formatScripture = (value: TScripture): string =>
  [...value.refs.map(formatScriptureRef), value.freeform.trim()]
    .filter((part) => part !== '')
    .join('; ');

/** True when the reference has no refs and no freeform text beyond whitespace. */
export const isScriptureEmpty = (value: TScripture): boolean =>
  value.refs.length === 0 && value.freeform.trim() === '';

/** A deep copy with new refs objects and a new books array, so the copy never aliases (or inherits the freeze of) its source. */
export const cloneScripture = (value: TScripture): TScripture => ({
  refs: value.refs.map((ref) => ({ ...ref })),
  freeform: value.freeform,
  books: [...value.books],
});
